// App Language locales and the two-level default (Final Implementation Authority §21.1;
// OWNER CONTRACT §9.2). Pure module: no store, no DOM — safe to import from anywhere.
//
//   OWNER / DEVELOPMENT ACCEPTANCE DEFAULT  = zh-CN  (initial dev builds, owner acceptance, device tests)
//   FINAL U.S. MARKET RELEASE DEFAULT       = en-US  (flipped ONLY by the release configuration below)
//
// The release flip is an env key, never a code edit: put VITE_APP_LANGUAGE_DEFAULT=en-US in the
// release env file. Any other / missing value fails closed to the owner acceptance default.

export type AppLocale = 'zh-CN' | 'en-US';

export const APP_LOCALES: readonly AppLocale[] = Object.freeze(['zh-CN', 'en-US']);

export const OWNER_ACCEPTANCE_DEFAULT_LOCALE: AppLocale = 'zh-CN';

export const US_MARKET_RELEASE_LOCALE: AppLocale = 'en-US';

export const RELEASE_LOCALE_ENV_KEY = 'VITE_APP_LANGUAGE_DEFAULT';

export function isAppLocale(value: unknown): value is AppLocale {
  return typeof value === 'string' && (APP_LOCALES as readonly string[]).includes(value);
}

// Explicit release configuration → default locale. Fail-closed: anything that is not exactly
// one of the two supported locales yields the owner acceptance default.
export function resolveReleaseDefaultLocale(raw: unknown): AppLocale {
  const value = typeof raw === 'string' ? raw.trim() : raw;
  return isAppLocale(value) ? value : OWNER_ACCEPTANCE_DEFAULT_LOCALE;
}

export const RELEASE_DEFAULT_LOCALE: AppLocale = resolveReleaseDefaultLocale(import.meta.env[RELEASE_LOCALE_ENV_KEY]);

// The App Language PREFERENCE (Owner task book 2026-09-04 §3): by default the app follows the
// device's system language; the inspector can pin 中文 or English in Settings (and only there).
export type AppLanguagePreference = AppLocale | 'system';

export const APP_LANGUAGE_PREFERENCE_ORDER: readonly AppLanguagePreference[] = Object.freeze(['system', 'zh-CN', 'en-US']);

export function isAppLanguagePreference(value: unknown): value is AppLanguagePreference {
  return value === 'system' || isAppLocale(value);
}

// Maps the device languages (navigator.languages order) onto the two supported locales:
// any Chinese variant → zh-CN, any English variant → en-US; nothing supported → null.
export function resolveSystemLocale(languages: readonly string[]): AppLocale | null {
  for (const raw of languages) {
    const tag = String(raw).toLowerCase();
    if (tag.startsWith('zh')) return 'zh-CN';
    if (tag.startsWith('en')) return 'en-US';
  }
  return null;
}

// "system" → the device language when supported, otherwise the release default (owner acceptance
// builds: zh-CN; U.S. market release: en-US). An explicit locale is used as-is.
export function resolvePreference(preference: AppLanguagePreference | null, languages: readonly string[], releaseDefault: AppLocale = RELEASE_DEFAULT_LOCALE): AppLocale {
  if (preference && preference !== 'system') return preference;
  return resolveSystemLocale(languages) ?? releaseDefault;
}
